import { Injectable, RendererFactory2, Renderer2, Inject } from '@angular/core'
import { Observable, BehaviorSubject } from 'rxjs'
import { DOCUMENT } from '@angular/common'

@Injectable({
    providedIn: 'root'
})
export class ThemeService {

    private _theme$: BehaviorSubject<string> = new BehaviorSubject<string>('default')

    theme$: Observable<string> = this._theme$.asObservable()

    private _renderer: Renderer2

    private head: HTMLElement

    private root: HTMLElement

    private themeLinks: HTMLElement[] = []

    constructor (
        rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) document: Document
    ) {
        this.head = document.head
        this.root = document.documentElement
        this._renderer = rendererFactory.createRenderer(null, null)
        this.theme$.subscribe(async (themeName: string) => {
            // <html data-theme> scopes the dark overrides (themes/dark.scss), which also set
            // color-scheme on panel pages only, so the public pages keep light native controls
            this._renderer.setAttribute(this.root, 'data-theme', themeName === 'dark' ? 'dark' : 'light')
            const cssExt = '.css'
            const cssFilename = themeName + cssExt
            await this.loadCss(cssFilename)
            if (this.themeLinks.length === 2) {
                this._renderer.removeChild(this.head, this.themeLinks.shift())
            }
        })
    }

    getTheme (): string {
        try {
            return localStorage.getItem('theme') === 'dark' ? 'dark' : 'default'
        } catch {
            return 'default'
        }
    }

    isDark (): boolean {
        return this._theme$.getValue() === 'dark'
    }

    setTheme (name: string) {
        try {
            localStorage.setItem('theme', name)
        } catch {
            // Storage blocked: the choice lasts for this page only
        }
        this._theme$.next(name)
    }

    private async loadCss (filename: string) {
        return new Promise(resolve => {
            const linkEl: HTMLElement = this._renderer.createElement('link')
            this._renderer.setAttribute(linkEl, 'rel', 'stylesheet')
            this._renderer.setAttribute(linkEl, 'type', 'text/css')
            this._renderer.setAttribute(linkEl, 'href', filename)
            this._renderer.setProperty(linkEl, 'onload', resolve)
            this._renderer.appendChild(this.head, linkEl)
            this.themeLinks = [...this.themeLinks, linkEl]
        })
    }
}
